import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import moment from 'moment';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import get from 'lodash/get';
import IconButton from 'components/IconButton';
import Button from 'components/Button';
import Spin from 'components/Spin';
import { MapClockIcon } from 'components/icons';
import * as Actions from 'store/modules/settings/actions';
import timezones from 'constants/timezones';
import { Helmet } from 'react-helmet-async';
import { toUserTimezone, formatPhoneNumber } from 'utils';
import toast, { snack } from 'utils/toast';
import { BUCKET_URL } from 'constants/index';
import vCard from 'lib/vacrd';
import ContactCardIcon from '@mui/icons-material/ContactMail';
import { EditIcon } from 'components/icons';
import useAlert from 'hooks/useAlert';
import PageWrapper from 'components/PageWrapper';
import useMediaUrl from '../../../hooks/useMediaURL';
import Connections from '../components/account/Connections';
import usePurchaseMessage from '../hooks/usePurchaseMessage';
import { getCountryName } from 'constants/countries';
import DLCStatus from 'constants/10-dlc-status';

export const Logo = ({ hash, onRemove }) => {
  const { url } = useMediaUrl(hash);

  return <img src={url} style={{ maxWidth: 150, width: '100%' }} />;
};

const AccountOverview = ({}) => {
  const dispatch = useDispatch();
  const account = useSelector((s) => s.settings.account);
  const user = useSelector((s) => s.user.user);
  const [t] = useTranslation('settings');
  const { showPurchaseModal, isAllowed } = usePurchaseMessage();
  const alert = useAlert();
  const status = account.account_10dlc_status;

  useEffect(() => {
    dispatch(Actions.loadUsage(account.account_id));
  }, []);

  const onEditCallerId = () => {
    alert.prompt({
      title: 'Edit Caller ID',
      description:
        'Up to 15 characters. Must start with a letter and can contain letters, numbers, periods, commas and spaces.',
      inputValue: account.account_cnam_name,
      confirmationText: 'Save',
      onConfirm: (val) => {
        return dispatch(
          Actions.updateAccount(account.account_id, {
            account_cnam_name: val,
          }),
        )
          .then(() => {
            snack.success('Caller ID updated');
          })
          .catch((e) => {
            toast.error(e.message);
          });
      },
    });
  };

  const { users_record_is_admin } = user;
  const isAdmin = users_record_is_admin === '1';
  const connLimit = get(account, 'usage.plan.connections', 0);
  const smsLimit = get(account, 'usage.plan.sms', 0);
  const smsSent = get(account, 'usage.counts.sms', 0);
  const smsPC = (smsSent * 100) / smsLimit;
  const timezone = timezones.find((t) => t.value === account.account_timezone);

  return (
    <PageWrapper>
      <Helmet title={t('account-overview-page-title', 'Settings > Account')} />
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={5} lg={4}>
          <Card
            sx={{
              mb: 3,
              position: 'relative',
            }}
          >
            <CardContent>
              <Box>
                {isAdmin && (
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 5,
                      right: 10,
                    }}
                    color="text.secondary"
                    icon={<EditIcon />}
                    to={`/settings/branding`}
                  >
                    Edit
                  </IconButton>
                )}
                <Box textAlign="center" mt={2}>
                  <Logo hash={account.account_logo_url} />
                </Box>
                <Box textAlign="center" mt={2}>
                  <Typography gutterBottom variant="h3" fontWeight={600}>
                    {account.account_name}
                  </Typography>
                  <Typography variant="body2">
                    {t('accountId', 'Account ID')}: {account.account_id}
                  </Typography>
                  {status === DLCStatus.COMPLETED && (
                    <Chip
                      rounded
                      sx={{ mt: 1 }}
                      size="small"
                      variant="outlined"
                      color="success"
                      label="10-DLC Verified"
                    />
                  )}

                  {isAdmin && user.user_onboarded !== '1' && (
                    <Link to="/setup">
                      {t('view-onboarding', 'View Onboarding')}
                    </Link>
                  )}
                  <Typography
                    gutterBottom
                    color="text.secondary"
                    variant="body2"
                    sx={{ mt: 2 }}
                  >
                    Address
                  </Typography>
                  <Typography>{account.account_address1}</Typography>
                  <Typography>{account.account_address2}</Typography>
                  <Typography>
                    {account.account_city}, {account.account_state}{' '}
                    {account.account_zip}
                  </Typography>
                  <Typography>
                    {getCountryName(account.account_country)}
                  </Typography>
                  <Box mt={2}>
                    <Typography>
                      Timezone: {timezone ? timezone.label : 'Not set'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card>
            <Stack
              py={1}
              px={2}
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
            >
              {isAdmin && (
                <IconButton
                  color="text.secondary"
                  icon={<EditIcon />}
                  to={`/settings/payment/edit`}
                >
                  Edit
                </IconButton>
              )}
            </Stack>
            <Box p={3} textAlign="center">
              <Typography gutterBottom variant="h3" fontWeight={600}>
                {account.account_first_name} {account.account_last_name}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                Primary Number
              </Typography>
              <Typography gutterBottom>
                {formatPhoneNumber(account.account_mobile)}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                Email
              </Typography>
              <Typography paragraph>{account.account_email}</Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={7} lg={8}>
          <Card sx={{ mb: 3 }}>
            <CardHeader
              titleTypographyProps={{
                variant: 'subtitle1',
                fontWeight: 500,
              }}
              title="Add another Phone Number"
              subheader={`Your current subscription includes a bundle of ${connLimit} Phone Numbers.`}
            />
            <CardContent>
              {isAdmin && !account.connections?.length ? (
                <Link to="/setup">
                  <Alert severity="info">
                    {t(
                      'select-phone-number-msg',
                      'Click here to select a Phone Number for your account.',
                    )}
                  </Alert>
                </Link>
              ) : null}
              {account.connections ? (
                <Connections account={account} isAdmin={isAdmin} />
              ) : (
                <Spin spinning />
              )}
              <Box mt={2}>
                <Typography fontWeight={500} variant="subtitle1">
                  Caller ID
                </Typography>
                <Typography paragraph color="text.secondary">
                  Manage your Caller ID
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="subtitle1">
                    {account.account_cnam_name}
                  </Typography>
                  <IconButton
                    size="small"
                    icon={<EditIcon />}
                    onClick={onEditCallerId}
                  />
                </Stack>
              </Box>
            </CardContent>
          </Card>
          <Card>
            {isAllowed && (
              <CardHeader
                titleTypographyProps={{
                  variant: 'subtitle1',
                  fontWeight: 500,
                }}
                title={t('need-more-msg', 'Need More Messages?')}
                subheaderTypographyProps={{
                  style: { display: 'inline' },
                }}
                subheader={
                  <>
                    {t(
                      'get-additional-500-msg',
                      'You can add 500 Text Messages per month for only $12.',
                    )}
                    &nbsp;
                    <Button
                      variant="link"
                      component={Link}
                      to="/marketplace"
                      sx={{ fontWeight: 500 }}
                    >
                      {t('get-more-msg', 'Get More Messages')}
                    </Button>
                  </>
                }
              />
            )}
            <CardContent>
              <Typography gutterBottom fontWeight={600}>
                {t('sms-sent-this-month')}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <LinearProgress
                  sx={{ height: 7, borderRadius: 1, width: '100%' }}
                  variant="determinate"
                  value={smsPC}
                />
                <Typography
                  fontWeight={500}
                  color="primary"
                  whiteSpace="nowrap"
                >
                  {smsSent} of {smsLimit}
                </Typography>
              </Stack>
              <Typography variant="caption" color="textSecondary">
                {t('counter-reset-on', {
                  defaultValue: 'Counters reset on {{date}} of every month.',
                  date:
                    account.usage &&
                    toUserTimezone(
                      account.usage.counts_reset,
                      account.account_timezone,
                    ).format('Do'),
                })}
              </Typography>
              <Box
                mt={4}
                mb={1}
                display="flex"
                justifyContent="space-between"
                component={Typography}
              >
                <span>Emails Sent This Month:</span>
                <span>
                  {get(account, 'usage.counts.email', 0)} of{' '}
                  {get(account, 'usage.plan.email')}
                </span>
              </Box>
              <Box
                mb={1}
                display="flex"
                justifyContent="space-between"
                component={Typography}
              >
                <span>Contact Records:</span>
                <span>
                  {get(account, 'usage.counts.contacts', 0)} of{' '}
                  {get(account, 'usage.plan.contacts')}
                </span>
              </Box>
              <Box
                mb={1}
                display="flex"
                justifyContent="space-between"
                component={Typography}
              >
                <span>Keywords:</span>
                <span>
                  {get(account, 'usage.counts.keywords', 0)} of Unlimited
                </span>
              </Box>
              <Box
                mb={1}
                display="flex"
                justifyContent="space-between"
                component={Typography}
              >
                <span>Connections:</span>
                <span>
                  {get(account, 'usage.counts.connections', 0)} of{' '}
                  {get(account, 'usage.plan.connections')}
                </span>
              </Box>
              <Box
                mb={1}
                display="flex"
                justifyContent="space-between"
                component={Typography}
              >
                <span>Callers:</span>
                <span>
                  {get(account, 'usage.counts.callers', 0)} of{' '}
                  {get(account, 'usage.plan.callers')}
                </span>
              </Box>
              <Box
                mb={1}
                display="flex"
                justifyContent="space-between"
                component={Typography}
              >
                <span>Talk minutes:</span>
                <span>
                  {get(account, 'usage.counts.talk_minutes', 0)} of{' '}
                  {get(account, 'usage.plan.talk_minutes')}
                </span>
              </Box>
            </CardContent>
          </Card>
          <Box mt={3}>
            <Card>
              <CardContent sx={{ pb: '16px !important' }}>
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h4" fontWeight={500}>
                    {t('after-hour-reply', 'After Hours Auto Reply')}{' '}
                    {account.account_away_message_enabled === '1' ? (
                      <Box component="span" sx={{ color: 'success.main' }}>
                        {t('enabled', 'Enabled')}
                      </Box>
                    ) : (
                      <Box component="span" sx={{ color: 'error.main' }}>
                        {t('disabled', 'Disabled')}
                      </Box>
                    )}
                  </Typography>
                  {isAdmin && (
                    <IconButton
                      color="text.secondary"
                      sx={{ ml: 1 }}
                      icon={<EditIcon />}
                      to="/settings/after-hours-reply"
                    />
                  )}
                </Stack>

                {account.account_away_message_enabled === '1' && (
                  <>
                    <div>
                      <strong>
                        {t('auto-reply-times', 'Auto Reply Times')}:
                      </strong>{' '}
                      {account.account_away_start_time &&
                      account.account_away_end_time
                        ? `${toUserTimezone(
                            account.account_away_start_time,
                            account.account_timezone,
                            'HH:mm:ss',
                          ).format('h:mm A')} to ${toUserTimezone(
                            account.account_away_end_time,
                            account.account_timezone,
                            'HH:mm:ss',
                          ).format('h:mm A')}`
                        : 'Not Set'}
                    </div>
                    <div>
                      <strong>
                        {t('auto-reply-msg', 'Auto Reply Message')}:
                      </strong>{' '}
                      {account.account_away_message}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </PageWrapper>
  );
};

export default AccountOverview;
